import { FC, useState, useRef, ReactElement } from "react";
import Modal from "react-modal";
import close from "assets/images/close.svg";
import Imgclose from "assets/images/input-img-close-icon.svg";
import { useSelector } from "react-redux";
import { dispatch } from "store";
import { setModalClosed } from "store/user-slice";
import "./style.css";
import Button from "components/particles/primary-button";
import csvIcon from "assets/images/csv.svg";
import zipIcon from "assets/images/zipfile.svg";
import { useBulkIngredients } from "./useHook";
import Swal from "sweetalert2";

const BulkIngredients: FC<{ getIngredients: Function }> = ({ getIngredients }): ReactElement => {
    const { bulkIngredientsModalIsOpen } = useSelector((state: any) => state.user);
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [zipFile, setZipFile] = useState<File | null>(null);
    const [error, setError] = useState<string>("");
    const csvInputRef = useRef<HTMLInputElement>(null);
    const zipInputRef = useRef<HTMLInputElement>(null);
    const { uploadBulkIngredients, generateCsvForIngredients } = useBulkIngredients();



    function closeModal() {
        dispatch(setModalClosed("Bulk Ingredients"));
        setCsvFile(null);
        setZipFile(null);
        setError("");
    }

    const handleCsvClick = () => {
        csvInputRef.current?.click();
    };

    const handleZipClick = () => {
        zipInputRef.current?.click();
    };

    const handleCsvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const fileExtension = file.name.split('.').pop()?.toLowerCase();

            if (fileExtension !== 'csv' && fileExtension !== 'xlsx') {
                setError("Please select an Excel (.xlsx) file");
                e.target.value = '';
                return;
            }

            const isValidCsv = file.type.match('text/csv') ||
                file.type.match('application/csv') ||
                file.type.match('text/x-csv') ||
                file.type.match('application/x-csv') ||
                file.type.match('text/comma-separated-values') ||
                file.type.match('text/x-comma-separated-values');

            const isValidExcel = file.type.match('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
                file.type.match('application/vnd.ms-excel') ||
                file.type === '';

            if (!isValidCsv && !isValidExcel && fileExtension !== 'xlsx' && fileExtension !== 'csv') {
                setError("Please select a valid Excel (.xlsx) file");
                e.target.value = '';
                return;
            }

            setCsvFile(file);
            setError("");
        }
    };

    const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setZipFile(e.target.files[0]);
            setError("");
        }
    };

    const handleRemoveCsvFile = () => {
        setCsvFile(null);
        if (csvInputRef.current) {
            csvInputRef.current.value = '';
        }
    };

    const handleRemoveZipFile = () => {
        setZipFile(null);
        if (zipInputRef.current) {
            zipInputRef.current.value = '';
        }
    };

    const handleSubmit = async () => {
        if (!csvFile) {
            setError("Excel file is required");
            return;
        }

        const formData = new FormData();
        formData.append("csvFile", csvFile);
        if (zipFile) {
            formData.append("imageZipFile", zipFile);
        }

        try {
            const response = await uploadBulkIngredients(formData);
            if (response?.status === 200) {
                await getIngredients();
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Bulk ingredients added successfully',
                });
                closeModal();
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to upload files. Please try again.',
            });
            setError("Failed to upload files. Please try again.");
        }
    };

    const handleDownload = async () => {
        try {
            await generateCsvForIngredients();
            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Template file downloaded successfully',
            });
        } catch (error: any) {
            let errorMessage = 'Failed to download template file. Please try again.';
            const data = error?.response?.data;
            if (data?.message && typeof data.message === 'string') {
                errorMessage = data.message;
            } else if (data instanceof Blob) {
                try {
                    const text = await data.text();
                    const parsed = JSON.parse(text);
                    if (parsed?.message) errorMessage = parsed.message;
                } catch {
                    // keep default errorMessage
                }
            }
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: errorMessage,
            });
        }
    };

    return (
        <Modal
            isOpen={bulkIngredientsModalIsOpen}
            onRequestClose={closeModal}
            className="styled-modal"
            contentLabel="Bulk Ingredients Modal"
        >
            <div className="bulk-ingredients-popup">
                <div className="close-button" onClick={closeModal}>
                    <img src={close} style={{ cursor: "pointer" }} alt="close" />
                </div>
                <div className="bulk-ingredients-popup-header">
                    <h1>Bulk Ingredients</h1>
                </div>
                <p className="bulk-ingredients-popup-header-link">
                    Click <span className="bulk-ingredients-popup-header-link-span" onClick={handleDownload} style={{ cursor: 'pointer', color: 'var(--reset-button)' }}>here</span> to download the sample Excel template for bulk ingredients.
                </p>
                <div className="bulk-ingredients-popup-content">
                    <div className="bulk-ingredients-popup-content-option">
                        <h2>Upload Excel file <span className="required">*</span></h2>
                        <input
                            ref={csvInputRef}
                            type="file"
                            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,.csv,text/csv,application/csv,text/x-csv,application/x-csv,text/comma-separated-values,text/x-comma-separated-values"
                            onChange={handleCsvChange}
                            required
                            style={{ display: 'none' }}
                        />
                        <div className="file-input-container">
                            <button className="file-input-button" onClick={handleCsvClick}>
                                <img src={csvIcon} alt="CSV" />
                                <div className="file-name-container">
                                    <span>{csvFile ? csvFile.name : 'Select Excel File'}</span>
                                    {csvFile && (
                                        <img
                                            className="close-icon"
                                            src={Imgclose}
                                            alt="Remove"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveCsvFile();
                                            }}
                                            title="Remove file"
                                        />
                                    )}
                                </div>
                            </button>
                        </div>
                        <p>Please upload an Excel (.xlsx) file with the following columns: name, image</p>
                    </div>
                    <div className="bulk-ingredients-popup-content-option">
                        <h2>Upload Image zip file (Optional)</h2>
                        <input
                            ref={zipInputRef}
                            type="file"
                            accept=".zip,application/zip,application/x-zip,application/x-zip-compressed"
                            onChange={handleZipChange}
                            style={{ display: 'none' }}
                        />
                        <div className="file-input-container">
                            <button className="file-input-button" onClick={handleZipClick}>
                                <img src={zipIcon} alt="ZIP" />
                                <div className="file-name-container">
                                    <span>{zipFile ? zipFile.name : 'Select ZIP File'}</span>
                                    {zipFile && (
                                        <img
                                            className="close-icon"
                                            src={Imgclose}
                                            alt="Remove"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveZipFile();
                                            }}
                                            title="Remove file"
                                        />
                                    )}
                                </div>
                            </button>
                        </div>
                        <p>Please upload a zip file with the following structure: ingredientname.jpg etc.</p>
                    </div>
                    {error && <div className="error-message">{error}</div>}
                    <div className="submit-button-container">
                        <Button
                            title="Upload Files"
                            width="16.5rem"
                            fill="true"
                            backgroundcolor={csvFile ? "var(--reset-button)" : "#ccc"}
                            onclick={csvFile ? handleSubmit : () => {
                                setError("Please select Excel file");
                            }}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default BulkIngredients;